import React, { ReactElement, useMemo } from 'react';
import { compose } from 'recompose';
import * as withContainer from '../../graphql/containers';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import { useDimensionColumns } from '../../utils/DimensionSets';
import Formatters from '../../components/grid-addons/formatters';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import DateField, { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import ValueField, { IValueFieldProps } from '../../components/connectedForm/ValueField';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import EditableDataGrid from '../../components/EditableDataGrid';
import { EntityType } from '../../@types/entityTypes';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { IEditableDataGridProps } from '../../components/EditableDataGrid/EditableDataGrid';
import { IBlurTextFieldProps } from '../../components/connectedForm/BlurTextField/BlurTextField';

const columnMeta: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Nominal',
    key: 'nominal.accountNo',
    width: 125,
    resizable: true,
  },
  {
    name: 'Description',
    key: 'nominal.name',
    resizable: true,
  },
  {
    name: 'Type',
    key: 'nominal.accountTypeDisplay',
    width: 150,
    resizable: true,
  },
  {
    name: 'Debit',
    key: 'baseDebit',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
  {
    name: 'Credit',
    key: 'baseCredit',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
];

const vatTypeColumn: IEditableDataGridColumnDef = {
  name: 'Type',
  key: 'vatType',
  formatter: Formatters.EnumFormatter,
  options: fieldMetaData.VatType,
};

const documentStatus = {
  key: 'documentStatus',
  status: [
    { value: 'DRAFT', style: 'default' },
    { value: 'POSTED', icon: 'check_circle', style: 'success' },
    { value: 'RELEASED', icon: 'watch_later', style: 'warning' },
  ],
};

const createPostingPreview: (
  entity: EntityType
) => ({ values, match }: { values: any; match?: { params?: any }; onClose?: () => void }) => ReactElement = (
  entity: EntityType
) => {
  //'values' here is 'any; because there's numerous possible types - Potential to replace with a union type of all the possible options.
  const NominalPostingPreviewForm = ({ values }: { values: any }) => {
    const columns = useDimensionColumns(columnMeta);

    const { manualVat, showGoodsVat, documentType, foreignCurrency } = values;
    const gridHeight = Math.floor((window.innerHeight - (foreignCurrency ? 380 : 320)) / 2);

    const { totalCredit, totalDebit } = useMemo(() => {
      return (
        values?.nominalPostingList?.reduce(
          (acc: { totalCredit: number; totalDebit: number }, { credit, debit }: any) => {
            acc.totalCredit += credit ?? 0;
            acc.totalDebit += debit ?? 0;
            return acc;
          },
          { totalCredit: 0, totalDebit: 0 }
        ) || { totalCredit: 0, totalDebit: 0 }
      );
    }, [values?.nominalPostingList]);

    const vatColumns = useMemo(() => {
      const columns = [
        {
          name: 'Authority',
          key: 'vatRate.vatAuthority.description',
          resizable: true,
        },
        {
          name: 'Business VAT Status',
          key: 'vatRate.vatBusinessCategory.description',
          resizable: true,
        },
        {
          name: 'Product VAT Category',
          key: 'vatRate.vatProductCategory.description',
          resizable: true,
        },
        {
          name: 'Rate',
          key: 'vatRate.rate',
          resizable: true,
          formatter: Formatters.NumericFormatter,
          options: {
            format: { decimals: 2, suffix: '%' },
          },
          cellClass: 'rightAlign',
          width: 100,
        },
        {
          name: 'Goods',
          key: 'baseGoods',
          resizable: true,
          formatter: Formatters.NumericFormatter,
          options: {
            format: { decimals: 2 },
          },
          cellClass: 'rightAlign',
          width: 150,
        },
        {
          name: 'VAT',
          key: 'baseVat',
          resizable: true,
          formatter: Formatters.NumericFormatter,
          options: {
            format: { decimals: 2 },
          },
          cellClass: 'rightAlign',
          width: 150,
        },
      ] as IEditableDataGridColumnDef[];

      if (documentType === 'NOMINAL_JOURNAL') {
        columns.push(vatTypeColumn);
      }

      return columns;
    }, [documentType, vatTypeColumn]);

    return (
      <div>
        <Form.FormContainer>
          <Form.Header
            entityHdr="documentTypeDisplay"
            entityHdr2="documentNumber"
            statusFlags={documentStatus}
            cancelText="Close"
            noDeleteOption={true}
            component={Form.HeaderBlock}
          />
          <Form.Form>
            <Form.Row>
              <Form.Fieldset cols={1} collapsed={true}>
                <Form.Legend label="Transaction Details" />
              </Form.Fieldset>
            </Form.Row>
            <Form.Row>
              <Form.Fieldset cols={2 / 12}>
                <Form.Field<IDateFieldProps>
                  dataid="documentDate"
                  component={DateField}
                  label="Document Date"
                  view={true}
                />
              </Form.Fieldset>
              <React.Fragment>
                <Form.Fieldset cols={2 / 12}>
                  <ValueField
                    data-testid="totalCredit"
                    label="Total Credit"
                    view={true}
                    value={totalCredit}
                    type={'currency'}
                    decimals={2}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={2 / 12}>
                  <ValueField
                    data-testid="totalDebit"
                    label="Total Debit"
                    view={true}
                    value={totalDebit}
                    type={'currency'}
                    decimals={2}
                  />
                </Form.Fieldset>
              </React.Fragment>
            </Form.Row>
            {foreignCurrency && documentType !== 'BANK_TRANSFER' ? (
              <Form.Row>
                <Form.Fieldset cols={2 / 12}>
                  <Form.Field<IValueFieldProps>
                    dataid="currency.description"
                    component={ValueField}
                    label="Currency"
                    view={true}
                  />
                </Form.Fieldset>
                <Form.Fieldset cols={2 / 12}>
                  <Form.Field<IValueFieldProps>
                    dataid="exchangeRate"
                    component={ValueField}
                    label="Exchange Rate"
                    decimals={2}
                    view={true}
                  />
                </Form.Fieldset>
                {showGoodsVat ? (
                  <React.Fragment>
                    <Form.Fieldset cols={2 / 12}>
                      <Form.Field<IValueFieldProps>
                        dataid="baseGoodsAmount"
                        component={ValueField}
                        label="Goods (GBP)"
                        decimals={2}
                        view={true}
                      />
                    </Form.Fieldset>
                    <Form.Fieldset cols={2 / 12}>
                      <Form.Field<IValueFieldProps>
                        dataid="baseVatAmount"
                        component={ValueField}
                        label="VAT (GBP)"
                        decimals={2}
                        view={true}
                        reformatLabel={false}
                      />
                    </Form.Fieldset>
                  </React.Fragment>
                ) : null}
                <Form.Fieldset cols={2 / 12}>
                  <Form.Field<IValueFieldProps>
                    dataid="baseAmount"
                    component={ValueField}
                    label="Total (GBP)"
                    decimals={2}
                    view={true}
                  />
                </Form.Fieldset>
              </Form.Row>
            ) : null}
            <Form.Row>
              <Form.Fieldset cols={1}>
                <Form.Legend label={`Nominal Transactions ${foreignCurrency ? '(GBP)' : ''}`} />
                <Form.Field<IEditableDataGridProps>
                  dataid="nominalPostingList"
                  component={EditableDataGrid}
                  label=""
                  columns={columns}
                  enableCellSelect={false}
                  addBlankRow={false}
                  contentHeight={gridHeight + 100}
                />
              </Form.Fieldset>
            </Form.Row>
            <Form.Row>
              {manualVat ? (
                <Form.Fieldset cols={1 / 2}>
                  <Form.Legend label={`VAT Transactions ${foreignCurrency ? '(GBP)' : ''}`} />
                  <Form.Field<IBlurTextFieldProps>
                    dataid="vatAuthority.description"
                    component={BlurTextField}
                    readOnly={true}
                    label="VAT Authority"
                    reformatLabel={false}
                  />
                  <Form.Field<IValueFieldProps>
                    dataid="baseVatAmount"
                    component={ValueField}
                    currency={true}
                    decimals={2}
                    label="VAT Amount"
                    reformatLabel={false}
                  />
                </Form.Fieldset>
              ) : (
                <Form.Fieldset cols={1}>
                  <Form.Legend label={`VAT Transactions ${foreignCurrency ? '(GBP)' : ''}`} />
                  <Form.Field<IEditableDataGridProps>
                    dataid="vatRateSummaryList"
                    component={EditableDataGrid}
                    label=""
                    columns={vatColumns}
                    enableCellSelect={false}
                    addBlankRow={false}
                    contentHeight={gridHeight - 100}
                  />
                </Form.Fieldset>
              )}
            </Form.Row>
          </Form.Form>
        </Form.FormContainer>
      </div>
    );
  };

  const connector = compose(
    withContainer.formQuery(entity.instance!),
    createForm({
      form: entity.type,
      name: entity.formTitle,
      entity: entity,
      queryFragment: entity.fragment,
      readOnly: true,
    })
  );
  return connector(NominalPostingPreviewForm);
};

export default createPostingPreview;
